import { fromEntries } from "src/utils/typeWrappers";

import MealFeedbackHeroImage from "@components/intro/images/meal-feedback.webp";
import WelliInsightHeroImage from "@components/intro/images/welli-insight.webp";
import MethodSelectorHeroImage from "@components/intro/images/method-selector.webp";
import WelliInsightGlp1CompanionHeroImage from "@components/intro/images/welli-insight-glp1-companion.webp";

export type InterstitialContext = (typeof CONTEXTS)[number];
export type InterstitialVersion = (typeof VERSIONS)[number];

const translationPrefix = "t:mealLoggingInterstitial";

/** UTM sources that cause the user to get redirected to the meal logging interstitial page */
export const ALLOWED_UTM_SOURCES = [
  // Method selector UTMs
  "meal_logging_method_selector_history_med_upgrade",
  "meal_logging_method_selector_history_weight_upgrade",
  "meal_logging_method_selector_barcode_med_upgrade",
  "meal_logging_method_selector_barcode_weight_upgrade",
  "meal_logging_method_selector_search_med_upgrade",
  "meal_logging_method_selector_search_weight_upgrade",
  // Method selector fallback UTMs
  "meal_feedback_med_upgrade",
  "meal_feedback_weight_upgrade",
  // Meal feedback content UTMs
  "meal_feedback_content_med_upgrade",
  "meal_feedback_content_weight_upgrade",
  "meal_feedback_content_weight_protect_muscles_glp1_companion",
  // Welli meal insight UTMs
  "welli_meal_insight_med_upgrade",
  "welli_meal_insight_weight_upgrade",
];

/*
 * BEFORE ADDING NEW CONTEXT OR VERSION:
 * 1. Add localization strings to src/locales/en-US/translations.json
 * 2. Add relevant UTM sources to ALLOWED_UTM_SOURCES
 * 3. Add detection logic to getMealLoggingInterstitialContext() and getMealLoggingInterstitialVersion()
 */
export const CONTEXTS = [
  "methodSelector" /** Users coming from meal logging method selector screens */,
  "mealFeedback" /** Users coming from meal feedback content */,
  "welliInsight" /** Users coming from Welli AI meal insight features */,
] as const;

export const VERSIONS = [
  "glp1Companion" /** Users on the free GLP-1 companion - evaluated first */,
  "noWeightLoss" /** Users without weight loss goal */,
  "weightLossNoMed" /** Users with weight loss goal, BMI < 25 */,
  "weightLossMed" /** Users with weight loss goal, BMI >= 25 */,
] as const;

export const HERO_IMAGES: Record<InterstitialContext, any> = {
  methodSelector: MethodSelectorHeroImage,
  mealFeedback: MealFeedbackHeroImage,
  welliInsight: WelliInsightHeroImage,
} as const;

export const GLP1_COMPANION_HERO_IMAGES: Record<InterstitialContext, any> = {
  methodSelector: MethodSelectorHeroImage,
  mealFeedback: MealFeedbackHeroImage,
  welliInsight: WelliInsightGlp1CompanionHeroImage,
} as const;

export const CONTENT_CONFIG = fromEntries(
  CONTEXTS.map((context) => [
    context,
    fromEntries(
      VERSIONS.map((version) => [
        version,
        {
          title: `${translationPrefix}:${context}:${version}:title`,
          cta: `${translationPrefix}:${context}:${version}:cta`,
          heroImage:
            version === "glp1Companion"
              ? GLP1_COMPANION_HERO_IMAGES[context]
              : HERO_IMAGES[context],
          bullets: [
            `${translationPrefix}:${context}:${version}:bullet1`,
            `${translationPrefix}:${context}:${version}:bullet2`,
            `${translationPrefix}:${context}:${version}:bullet3`,
          ],
        } as const,
      ])
    ),
  ])
);
